/**
 * Requesters, departments and locations — Step 104.
 *
 * From the reference's User Management. Three tabs in the URL. The requester
 * editor is draft-first for the record and the custom fields, with ONE atomic
 * act outside the draft — Deactivate — for the same reason as agents.
 *
 * ── What "corrected" means here ────────────────────────────────────────
 *
 * Every directory column you set is LOCKED against the Entra sync from then
 * on, and the editor says which ones are. Without that the sync would put the
 * wrong department back an hour later and the fix would look like it never
 * happened. Unlocking is a deliberate click, not a side effect of saving.
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Lock, LockOpen, Plus, Search, ShieldCheck, UserMinus, Users } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Select } from '../../components/ui/select';
import { Skeleton } from '../../components/ui/skeleton';
import { Badge } from '../../components/ui/badge';
import { FieldRenderer } from '../../features/fields/FieldRenderer';
import { apiFetch, apiSend } from '../../lib/api';
import { qk } from '../../lib/queryClient';
import { useUrlState } from '../../lib/urlState';
import { useDirty } from '../../lib/dirty';
import { useConfirm } from '../../lib/confirm';
import { relativeTime } from '../../lib/time';
import { cn } from '../../lib/utils';
import type { ResolvedField } from '@itsm/shared';

interface Named {
  id: string;
  name: string;
  isActive: number;
}
interface Requester {
  id: string;
  name: string | null;
  email: string;
  isActive: boolean;
  jobTitle: string | null;
  department: string | null;
  departmentId: string | null;
  officeLocation: string | null;
  locationId: string | null;
  mobilePhone: string | null;
  businessPhone: string | null;
  lastLoginAt: number | null;
  entraSyncedAt: number | null;
  entraOid: string | null;
  lockedFields: string[];
  fields: Record<string, unknown>;
}
interface Payload {
  entries: Requester[];
  departments: Named[];
  locations: Named[];
  profileFields: ResolvedField[];
  profileLayoutId: string | null;
}

export function RequestersPage() {
  const [tabRaw, setTab] = useUrlState('tab', 'people');
  const tab = tabRaw === 'departments' || tabRaw === 'locations' ? tabRaw : 'people';
  return (
    <div className="p-6">
      <div className="mb-3 flex items-center gap-3">
        <Users className="h-5 w-5 text-muted-foreground" />
        <h1 className="text-lg font-semibold">Requesters</h1>
      </div>
      <div className="mb-5 flex gap-1 border-b">
        {(['people', 'departments', 'locations'] as const).map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={cn(
              '-mb-px border-b-2 px-3 py-2 text-sm font-medium capitalize focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
              tab === t
                ? 'border-brand text-foreground'
                : 'border-transparent text-muted-foreground hover:text-foreground',
            )}
          >
            {t}
          </button>
        ))}
      </div>
      {tab === 'people' && <PeopleTab />}
      {tab === 'departments' && <ListTab kind="departments" />}
      {tab === 'locations' && <ListTab kind="locations" />}
    </div>
  );
}

function PeopleTab() {
  const { me } = { me: null }; // requesters never edit themselves here
  void me;
  const [selectedId, setSelectedId] = useUrlState('person', null);
  const [q, setQ] = useUrlState('q', '');
  const [showInactive, setShowInactive] = useState(false);
  const loaded = useQuery({
    queryKey: qk.adminRequesters,
    queryFn: () => apiFetch<Payload>('/api/admin/requesters'),
  });

  if (loaded.isError)
    return (
      <div className="rounded-lg border border-destructive/40 bg-destructive/5 p-4">
        <p className="text-sm font-medium">Requesters could not be loaded.</p>
        <Button className="mt-2" size="sm" variant="outline" onClick={() => void loaded.refetch()}>
          Retry
        </Button>
      </div>
    );
  if (loaded.isPending) return <Skeleton className="h-64 w-full" />;

  const d = loaded.data;
  const needle = (q ?? '').trim().toLowerCase();
  const shown = d.entries.filter(
    (p) =>
      (showInactive || p.isActive) &&
      (!needle ||
        [p.name, p.email, p.jobTitle, p.department, p.officeLocation]
          .filter(Boolean)
          .some((f) => String(f).toLowerCase().includes(needle))),
  );
  const inactive = d.entries.filter((p) => !p.isActive).length;
  const selected = d.entries.find((p) => p.id === selectedId) ?? null;
  const depName = (id: string | null) => d.departments.find((x) => x.id === id)?.name;
  const locName = (id: string | null) => d.locations.find((x) => x.id === id)?.name;

  return (
    <div className="grid gap-6 lg:grid-cols-[1fr,24rem]">
      <div>
        <div className="mb-3 flex flex-wrap items-center gap-2">
          <div className="relative">
            <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
            <Input
              className="h-8 w-64 pl-7 text-sm"
              placeholder="Search name, email, department…"
              value={q ?? ''}
              onChange={(e) => setQ(e.target.value)}
            />
          </div>
          <p className="text-sm text-muted-foreground">
            {shown.length} {shown.length === 1 ? 'person' : 'people'}
            {needle && shown.length !== d.entries.length && ` of ${d.entries.length}`}
          </p>
          {inactive > 0 && (
            <label className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <input
                type="checkbox"
                className="h-3.5 w-3.5 rounded border-input"
                checked={showInactive}
                onChange={(e) => setShowInactive(e.target.checked)}
              />
              Show {inactive} deactivated
            </label>
          )}
        </div>
        <div className="overflow-x-auto rounded-lg border bg-card">
          <table className="w-full min-w-[40rem] text-sm">
            <thead className="bg-muted/40 text-xs">
              <tr>
                <th className="px-3 py-2 text-left font-medium">Person</th>
                <th className="px-3 py-2 text-left font-medium">Department</th>
                <th className="px-3 py-2 text-left font-medium">Location</th>
                <th className="px-3 py-2 text-left font-medium">Last sign-in</th>
              </tr>
            </thead>
            <tbody>
              {shown.map((p) => (
                <tr
                  key={p.id}
                  onClick={() => setSelectedId(p.id)}
                  className={cn(
                    'cursor-pointer border-t hover:bg-accent/40',
                    selectedId === p.id && 'bg-brand/5',
                    !p.isActive && 'opacity-60',
                  )}
                >
                  <td className="px-3 py-2">
                    <p className="truncate font-medium">
                      {p.name ?? p.email}
                      {!p.isActive && (
                        <Badge variant="secondary" className="ml-1.5">
                          Deactivated
                        </Badge>
                      )}
                      {p.lockedFields.length > 0 && (
                        <Lock
                          className="ml-1.5 inline h-3 w-3 text-muted-foreground"
                          aria-label="Has hand-set fields"
                        />
                      )}
                    </p>
                    <p className="truncate text-xs text-muted-foreground">
                      {p.email}
                      {p.jobTitle && ` · ${p.jobTitle}`}
                    </p>
                  </td>
                  <td className="px-3 py-2 text-muted-foreground">
                    {depName(p.departmentId) ?? p.department ?? '—'}
                  </td>
                  <td className="px-3 py-2 text-muted-foreground">
                    {locName(p.locationId) ?? p.officeLocation ?? '—'}
                  </td>
                  <td className="px-3 py-2 text-xs text-muted-foreground">
                    {p.lastLoginAt ? relativeTime(p.lastLoginAt) : 'Never'}
                    {p.entraOid && <span className="ml-1">· Microsoft</span>}
                  </td>
                </tr>
              ))}
              {shown.length === 0 && (
                <tr>
                  <td colSpan={4} className="px-3 py-8 text-center text-sm text-muted-foreground">
                    Nobody matches.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <p className="mt-2 text-xs text-muted-foreground">
          Requesters are created when they raise a ticket or sign in; there is nothing to add here. Custom
          profile fields are edited under Ticket fields → Requester profile.
        </p>
      </div>
      <div>
        {selected ? (
          <RequesterEditor
            key={selected.id}
            person={selected}
            payload={d}
            onClose={() => setSelectedId(null)}
          />
        ) : (
          <p className="rounded-lg border border-dashed p-8 text-center text-sm text-muted-foreground">
            Pick a person to see and correct their record.
          </p>
        )}
      </div>
    </div>
  );
}

const LOCKABLE: { key: 'name' | 'jobTitle' | 'mobilePhone' | 'businessPhone'; label: string }[] = [
  { key: 'name', label: 'Name' },
  { key: 'jobTitle', label: 'Job title' },
  { key: 'mobilePhone', label: 'Mobile' },
  { key: 'businessPhone', label: 'Phone' },
];

function RequesterEditor({
  person,
  payload,
  onClose,
}: {
  person: Requester;
  payload: Payload;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const confirm = useConfirm();
  const stored = {
    name: person.name ?? '',
    jobTitle: person.jobTitle ?? '',
    mobilePhone: person.mobilePhone ?? '',
    businessPhone: person.businessPhone ?? '',
    departmentId: person.departmentId ?? '',
    locationId: person.locationId ?? '',
    fields: person.fields,
  };
  const [draft, setDraft] = useState(stored);
  const [error, setError] = useState<string | null>(null);
  const dirty = JSON.stringify(draft) !== JSON.stringify(stored);
  useDirty(dirty);

  const save = useMutation({
    mutationFn: () =>
      apiSend<{ lockedFields: string[] }>(`/api/admin/requesters/${person.id}`, 'PATCH', {
        name: draft.name.trim(),
        jobTitle: draft.jobTitle.trim() || null,
        mobilePhone: draft.mobilePhone.trim() || null,
        businessPhone: draft.businessPhone.trim() || null,
        departmentId: draft.departmentId || null,
        locationId: draft.locationId || null,
        fields: draft.fields,
      }),
    onSuccess: (res) => {
      if (!res.ok) return setError(res.error ?? 'That could not be saved.');
      setError(null);
      void qc.invalidateQueries({ queryKey: qk.adminRequesters });
      toast.success('Saved. Hand-set fields are now protected from directory sync.');
    },
  });
  const act = useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      apiSend(`/api/admin/requesters/${person.id}`, 'PATCH', body),
    onSuccess: (res) => {
      if (!res.ok) return toast.error(res.error ?? 'That did not work.');
      void qc.invalidateQueries({ queryKey: qk.adminRequesters });
    },
  });

  return (
    <div className="rounded-lg border bg-card">
      <div className="border-b p-4">
        <p className="truncate font-semibold">{person.name ?? person.email}</p>
        <p className="truncate text-xs text-muted-foreground">{person.email}</p>
        {person.entraSyncedAt && (
          <p className="mt-1 text-xs text-muted-foreground">
            Synced from Microsoft {relativeTime(person.entraSyncedAt)}.
            {person.lockedFields.length > 0 &&
              ` ${person.lockedFields.length} field${person.lockedFields.length === 1 ? '' : 's'} set by hand and protected.`}
          </p>
        )}
      </div>
      <div className="space-y-3 p-4">
        {LOCKABLE.map((f) => (
          <div key={f.key}>
            <label className="mb-1 flex items-center gap-1 text-xs font-medium text-muted-foreground">
              {f.label}
              {person.lockedFields.includes(f.key) && (
                <Lock className="h-3 w-3" aria-label="Protected from sync" />
              )}
            </label>
            <Input value={draft[f.key]} onChange={(e) => setDraft({ ...draft, [f.key]: e.target.value })} />
          </div>
        ))}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="mb-1 block text-xs font-medium text-muted-foreground">Department</label>
            <Select
              value={draft.departmentId}
              onChange={(e) => setDraft({ ...draft, departmentId: e.target.value })}
            >
              <option value="">—{person.department ? ` (directory: ${person.department})` : ''}</option>
              {payload.departments
                .filter((x) => x.isActive || x.id === draft.departmentId)
                .map((x) => (
                  <option key={x.id} value={x.id}>
                    {x.name}
                  </option>
                ))}
            </Select>
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-muted-foreground">Location</label>
            <Select
              value={draft.locationId}
              onChange={(e) => setDraft({ ...draft, locationId: e.target.value })}
            >
              <option value="">
                —{person.officeLocation ? ` (directory: ${person.officeLocation})` : ''}
              </option>
              {payload.locations
                .filter((x) => x.isActive || x.id === draft.locationId)
                .map((x) => (
                  <option key={x.id} value={x.id}>
                    {x.name}
                  </option>
                ))}
            </Select>
          </div>
        </div>
        {payload.profileFields.length > 0 && (
          <div className="border-t pt-3">
            <p className="mb-2 text-xs font-medium text-muted-foreground">Profile</p>
            <FieldRenderer
              fields={payload.profileFields}
              values={draft.fields}
              onChange={(key, value) => setDraft({ ...draft, fields: { ...draft.fields, [key]: value } })}
              surface="agent"
            />
          </div>
        )}
        {error && <p className="text-xs text-destructive">{error}</p>}
        <div className="flex items-center justify-end gap-2 border-t pt-3">
          {dirty && <span className="text-xs text-muted-foreground">Unsaved changes</span>}
          <Button
            variant="ghost"
            size="sm"
            disabled={!dirty || save.isPending}
            onClick={() => setDraft(stored)}
          >
            Discard
          </Button>
          <Button
            size="sm"
            disabled={!dirty || save.isPending || !draft.name.trim()}
            onClick={() => save.mutate()}
          >
            {save.isPending ? 'Saving…' : 'Save'}
          </Button>
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-2 border-t p-4">
        {person.lockedFields.length > 0 && (
          <Button
            variant="outline"
            size="sm"
            onClick={async () => {
              if (
                await confirm({
                  title: 'Let directory sync write these fields again?',
                  body: `${person.lockedFields.length} field(s) you set by hand will be overwritten on the next sync.`,
                  confirmLabel: 'Unlock',
                })
              )
                act.mutate({ unlockFields: true });
            }}
          >
            <LockOpen /> Unlock for sync
          </Button>
        )}
        <div className="flex-1" />
        {person.isActive ? (
          <Button
            variant="outline"
            size="sm"
            className="text-destructive"
            onClick={async () => {
              if (
                await confirm({
                  title: `End ${person.name ?? person.email}'s access?`,
                  body: 'They cannot sign in to the portal. Their tickets are kept. You can restore access later.',
                  confirmLabel: 'Deactivate',
                  destructive: true,
                })
              )
                act.mutate({ isActive: false });
            }}
          >
            <UserMinus /> Deactivate
          </Button>
        ) : (
          <Button variant="outline" size="sm" onClick={() => act.mutate({ isActive: true })}>
            <ShieldCheck /> Restore access
          </Button>
        )}
      </div>
      <div className="border-t px-4 py-2 text-right">
        <button onClick={onClose} className="text-xs text-muted-foreground hover:underline">
          Close
        </button>
      </div>
    </div>
  );
}

/** Departments and locations share one shape: name, archive, restore. */
function ListTab({ kind }: { kind: 'departments' | 'locations' }) {
  const qc = useQueryClient();
  const [name, setName] = useState('');
  const [renaming, setRenaming] = useState<string | null>(null);
  const [renameDraft, setRenameDraft] = useState('');
  const [error, setError] = useState<string | null>(null);
  const loaded = useQuery({
    queryKey: qk.orgList(kind),
    queryFn: () => apiFetch<{ entries: Named[] }>(`/api/org/${kind}`),
  });
  const renameDirty =
    renaming !== null &&
    renameDraft.trim() !== '' &&
    renameDraft.trim() !== loaded.data?.entries.find((x) => x.id === renaming)?.name;
  useDirty(renameDirty);
  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: qk.orgList(kind) });
    void qc.invalidateQueries({ queryKey: qk.adminRequesters });
  };
  const act = useMutation({
    mutationFn: ({ path, method, body }: { path: string; method: string; body?: unknown }) =>
      apiSend(path, method, body),
    onSuccess: (res) => {
      setError(res.ok ? null : (res.error ?? 'That did not work.'));
      if (res.ok) invalidate();
    },
  });
  if (loaded.isPending) return <Skeleton className="h-40 w-full max-w-xl" />;
  if (loaded.isError) return <p className="text-sm text-destructive">Could not load {kind}.</p>;
  const singular = kind === 'departments' ? 'department' : 'location';
  return (
    <div className="max-w-xl">
      <p className="mb-3 text-sm text-muted-foreground">
        The desk's own list — what tickets are routed and reported by. Archive rather than delete: an entry
        going away must not blank the field on everyone who carried it.
      </p>
      <form
        className="mb-3 flex gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          if (!name.trim()) return;
          act.mutate({ path: `/api/org/${kind}`, method: 'POST', body: { name: name.trim() } });
          setName('');
        }}
      >
        <Input placeholder={`New ${singular}…`} value={name} onChange={(e) => setName(e.target.value)} />
        <Button type="submit" disabled={!name.trim() || act.isPending}>
          <Plus /> Add
        </Button>
      </form>
      {error && <p className="mb-2 text-xs text-destructive">{error}</p>}
      <div className="divide-y rounded-lg border bg-card">
        {loaded.data.entries.map((x) => (
          <div
            key={x.id}
            className={cn('flex items-center gap-2 px-3 py-2 text-sm', !x.isActive && 'opacity-60')}
          >
            {renaming === x.id ? (
              <form
                className="flex flex-1 gap-2"
                onSubmit={(e) => {
                  e.preventDefault();
                  if (!renameDirty) return;
                  act.mutate({
                    path: `/api/org/${kind}/${x.id}`,
                    method: 'PATCH',
                    body: { name: renameDraft.trim() },
                  });
                  setRenaming(null);
                }}
              >
                <Input
                  autoFocus
                  value={renameDraft}
                  onChange={(e) => setRenameDraft(e.target.value)}
                  className="h-8"
                  aria-label={`${singular} name`}
                />
                <Button type="submit" size="sm" disabled={!renameDirty}>
                  Save
                </Button>
                <Button type="button" size="sm" variant="ghost" onClick={() => setRenaming(null)}>
                  Cancel
                </Button>
              </form>
            ) : (
              <>
                <span className="flex-1 truncate">
                  {x.name}
                  {!x.isActive && (
                    <Badge variant="secondary" className="ml-1.5">
                      Archived
                    </Badge>
                  )}
                </span>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setRenameDraft(x.name);
                    setRenaming(x.id);
                  }}
                >
                  Rename
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() =>
                    act.mutate({
                      path: `/api/org/${kind}/${x.id}`,
                      method: 'PATCH',
                      body: { isActive: !x.isActive },
                    })
                  }
                >
                  {x.isActive ? 'Archive' : 'Restore'}
                </Button>
              </>
            )}
          </div>
        ))}
        {loaded.data.entries.length === 0 && (
          <p className="px-3 py-6 text-center text-sm text-muted-foreground">None yet.</p>
        )}
      </div>
    </div>
  );
}
